import { useEffect, useState } from 'react';
import { tReaction } from 'src/components/Chat/Chat.types';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

export type tReactionPillProps = {
  reactions: tReaction[];
  fromMe: boolean;
  /**
   * Toggles the business's own reaction. Left out on channels Leadsales
   * cannot react on (Messenger, Instagram — INT-1702): each reaction is then
   * drawn display-only.
   */
  onToggleReaction?: (emoji: string) => void;
};

export default function ReactionPill(props: tReactionPillProps) {
  const { reactions, fromMe, onToggleReaction } = props;
  const { t } = useTranslation('chat/chat');

  return (
    <div
      className={classnames('flex gap-1 mt-0.5', {
        'justify-end': !fromMe,
        'justify-start': fromMe,
      })}
    >
      {reactions.map((reaction) => {
        const key = `${reaction.fromMe ? 'me' : 'them'}-${reaction.emoji}-${reaction.emojiUrl ?? ''}`;
        const label = t('reactions.reactionLabel', {
          emoji: reaction.emoji,
          owner: reaction.fromMe ? t('reactions.you') : '',
        });
        const className = classnames(
          'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs',
          'bg-base-200',
          'animate-pill-appear',
          { 'ring-1 ring-primary/30': reaction.fromMe },
        );
        const emoji = (
          <ReactionEmoji emoji={reaction.emoji} url={reaction.emojiUrl} />
        );

        if (!onToggleReaction) {
          return (
            <span key={key} role="img" className={className} aria-label={label}>
              {emoji}
            </span>
          );
        }

        return (
          <button
            key={key}
            className={classnames(
              className,
              'hover:bg-base-300 transition-all duration-200',
            )}
            onClick={() => {
              if (reaction.fromMe) {
                onToggleReaction(reaction.emoji);
              }
            }}
            aria-label={label}
            aria-pressed={reaction.fromMe}
          >
            {emoji}
          </button>
        );
      })}
    </div>
  );
}

function ReactionEmoji({ emoji, url }: { emoji: string; url?: string | null }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  return (
    <span className="text-sm leading-none">
      {url?.startsWith('https://') && !failed ? (
        <img
          src={url}
          alt={emoji}
          className="w-5 h-5 object-contain"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        emoji
      )}
    </span>
  );
}
